import umlCanvasEngine from '../UmlCanvasEngine';
import { formatSeqParams } from '../CsSeqDia/CsSeqParamsFormatter';

const LABEL_FONT = '600 11px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'; // approximation — the CSS doesn't set its own font-family (inherits), close enough for layout purposes
const START_MARGIN = 20; // min px between a message's own left edge (the caller's lifeline) and where its label TEXT starts — generalizes the earlier third-level-only 40px fix (chat 2026-07-19) to every message, lowered to 20 on user feedback (2026-07-19)
const END_MARGIN = 40; // min px of breathing room past the label's rendered text before the arrival side
const WIDEN_BUFFER = 10; // extra slack added on top of the bare-minimum required width — a gap sized to fit EXACTLY (zero slack) can still visually read as touching/cramped
const MIN_EDGE_GAP = 24; // baseline breathing room between two participant boxes' edges when no message directly needs more (matches CsSeqCanvasLayoutBuilder's own generation-time PARTICIPANT_GAP)
const MIN_PACKAGE_GAP = 40; // baseline breathing room between two DIFFERENT packages/actors (matches CsSeqCanvasLayoutBuilder's own generation-time NAMESPACE_GAP) — a separate, usually-larger constant because it's measured from the PACKAGE's own edge, not the participant's

let measureCtx = null;

/**
 * Widest \n-split line of `text`, rendered at LABEL_FONT. Deliberately NOT
 * capped at LABEL_BOX_WIDTH — the CSS sets no word-break/overflow-wrap, so a
 * single unbroken token (a generic type, a dotted `Field.Property.Method`
 * path — exactly the labels CsSeqDia produces) genuinely overflows the
 * 180px box instead of wrapping onto a second line. Capping this made the
 * collision math think a long label was narrower than it really renders
 * (chat 2026-07-19 bug: `Task<CmcFearGreedHistoricalResponse>` still landed
 * on top of an activation bar after "nudging"). labelCollidesWithActivations
 * already handles textWidth > LABEL_BOX_WIDTH correctly (textLeft goes
 * negative relative to labelLeft, exactly matching how the overflow
 * actually renders).
 * @param {string} text
 * @returns {number}
 */
function measureLabelWidth(text) {
  if (!measureCtx) {
    measureCtx = document.createElement('canvas').getContext('2d');
  }
  measureCtx.font = LABEL_FONT;
  return (text || '').split('\n').reduce(function trackWidest(max, line) {
    return Math.max(max, measureCtx.measureText(line).width);
  }, 0);
}

/**
 * A 'package' shape with at least one seq-participant child belongs to a
 * sequence diagram — 'package' itself is shared with class diagrams, so
 * this is how a seq-dia namespace box is told apart from an unrelated one.
 * @param {object} shape
 * @returns {boolean}
 */
function isSeqPackage(shape) {
  return shape.kind === 'package' && Array.isArray(shape.children) && shape.children.some(function hasSeqChild(c) { return c.kind === 'seq-participant'; });
}

/**
 * Gathers everything this pass needs straight off the LIVE canvas — always
 * the current geometry, including anything the coder already dragged by
 * hand. `unit` on each participant entry is the shape that actually MOVES
 * it on canvas: the owning package for a nested participant (moving the
 * package cascades to every child via UmlCanvasEngine.translateChildren),
 * or the actor shape itself for a standalone actor.
 * @returns {{participants: {shape: object, unit: object}[], messages: object[], activations: object[]}}
 */
function collectSeqDiaData() {
  const participants = [];
  umlCanvasEngine.shapes.filter(isSeqPackage).forEach(function collectPackageChildren(pkg) {
    pkg.children.forEach(function collectChild(child) {
      if (child.kind === 'seq-participant' && !child.linkedParticipantId) {
        participants.push({ shape: child, unit: pkg });
      }
    });
  });
  umlCanvasEngine.shapes
    .filter(function isTopActor(s) { return s.kind === 'seq-participant' && s.isActor && !s.linkedParticipantId; })
    .forEach(function collectActor(a) { participants.push({ shape: a, unit: a }); });

  const messages = umlCanvasEngine.shapes.filter(function isMessage(s) { return s.kind === 'seq-message'; });
  const activations = umlCanvasEngine.shapes.filter(function isActivation(s) { return s.kind === 'seq-activation'; });

  return { participants, messages, activations };
}

/**
 * True if a label's rendered text box [labelLeft, labelLeft+textWidth] x
 * [message.y, message.y+message.h] overlaps ANY activation bar's CURRENT
 * on-canvas rectangle. Left-aligned text (see UmlSeqMessageWidget.css) means
 * the visible characters start exactly at labelLeft — no box-width offset
 * math needed, unlike the old right-aligned version.
 * @param {number} labelLeft
 * @param {number} textWidth
 * @param {object} message
 * @param {object[]} activations
 * @returns {boolean}
 */
function labelCollidesWithActivations(labelLeft, textWidth, message, activations) {
  const labelTop = message.y + (message.label.y || 0);
  const labelBottom = labelTop + message.h;
  return activations.some(function overlaps(a) {
    const xOverlap = labelLeft < a.x + a.w && labelLeft + textWidth > a.x;
    const yOverlap = labelTop < a.y + a.h && labelBottom > a.y;
    return xOverlap && yOverlap;
  });
}

/**
 * Groups messages into [call, return] pairs via pairedMessageId (a boundary
 * call with no return, or an entry step whose partner isn't in this list,
 * comes back as a singleton `[message]`). A call and its own return ALWAYS
 * share the exact same `message.x` — CsSeqShapeFactory draws every call
 * rightward and every return leftward (see its own doc), and both halves of
 * one pair touch the same two participants, so `min(fromX, toX)` lands on
 * the CALLER's x either way. That shared x is exactly what lets both
 * labels use one common offset and land on the same left edge.
 * @param {object[]} messages
 * @returns {object[][]}
 */
function buildPairs(messages) {
  const seen = new Set();
  const pairs = [];
  messages.forEach(function toPair(m) {
    if (seen.has(m.id)) return;
    seen.add(m.id);
    const partner = m.pairedMessageId ? messages.find(function findPartner(o) { return o.id === m.pairedMessageId; }) : null;
    if (partner) {
      seen.add(partner.id);
      pairs.push(m.isReturn ? [partner, m] : [m, partner]);
    } else {
      pairs.push([m]);
    }
  });
  return pairs;
}

/**
 * The exact text UmlSeqMessageWidget renders for this message right now —
 * base label, plus the parameter suffix if the toggle (hotkey P) currently
 * shows one (call messages only, matching the widget's own `!shape.isReturn`
 * check). Measuring anything less than this is what let a params-widened
 * label still collide after auto-layout (chat 2026-07-19 bug): the pass only
 * ever measured the bare method name, never the parameters shown alongside it.
 * @param {object} message
 * @returns {string}
 */
function fullDisplayText(message) {
  if (message.isReturn) return message.label.text;
  const paramsText = formatSeqParams(message.params, umlCanvasEngine.viewConfig.seqParamsMode);
  return paramsText ? `${message.label.text} ${paramsText}` : message.label.text;
}

/**
 * STEP 1 — label placement. EVERY pair gets exactly START_MARGIN, always —
 * a fixed margin is what actually delivers "every call and its return line
 * up under each other, right next to the caller" (chat 2026-07-19). Gap
 * sizing itself is normalizeGaps' job (Step 2) — it always runs right after
 * this and computes exactly how much room START_MARGIN's worth of label
 * needs, so there's no need to reactively check for collisions here first.
 * @param {object[][]} pairs
 */
function nudgeLabels(pairs) {
  pairs.forEach(function nudgePair(pair) {
    pair.forEach(function applyMargin(m) { m.label.x = START_MARGIN; });
  });
}

/**
 * @param {object} shape
 * @returns {number}
 */
function centerX(shape) {
  return shape.x + shape.w / 2;
}

/**
 * Required center-to-center distance for a SINGLE gap. Two cases:
 * - Same package/actor unit: participant-edge math is enough (the package
 *   resizes to match, see applyGapDelta) — half-widths + MIN_EDGE_GAP.
 * - DIFFERENT units: participant-edge math alone isn't enough — a package's
 *   own box (title bar included, see CsSeqCanvasLayoutBuilder's
 *   nameBasedWidth) is often wider than its participants, so shrinking
 *   purely on participant width can make the PACKAGE BOXES overlap even
 *   though their participants have plenty of room (chat 2026-07-19 bug).
 *   Measured from each unit's own edge instead: how far the unit's edge
 *   sits past its own participant's center, plus MIN_PACKAGE_GAP, on both
 *   sides. For an actor (unit === its own shape) this reduces to exactly
 *   the participant-half-width case, since there's no extra package chrome.
 * Either way, never less than the widest content-driven requirement among
 * the DIRECT messages touching both these participants.
 * @param {{shape: object, unit: object}} p1
 * @param {{shape: object, unit: object}} p2
 * @param {object[][]} pairs
 * @returns {number}
 */
function requiredDistanceBetween(p1, p2, pairs) {
  const minDistance = p1.unit === p2.unit
    ? p1.shape.w / 2 + p2.shape.w / 2 + MIN_EDGE_GAP
    : ((p1.unit.x + p1.unit.w) - centerX(p1.shape)) + MIN_PACKAGE_GAP + (centerX(p2.shape) - p2.unit.x);

  const directPairs = pairs.filter(function isDirect(pair) {
    const m = pair[0];
    return (m.fromParticipantId === p1.shape.id && m.toParticipantId === p2.shape.id) ||
      (m.fromParticipantId === p2.shape.id && m.toParticipantId === p1.shape.id);
  });
  const contentWidth = directPairs.reduce(function trackMax(max, pair) {
    const widths = pair.map(function measure(m) { return measureLabelWidth(fullDisplayText(m)); });
    return Math.max(max, START_MARGIN + Math.max.apply(null, widths) + END_MARGIN + WIDEN_BUFFER);
  }, 0);
  return Math.max(minDistance, contentWidth);
}

/**
 * STEP 2 — recomputes EVERY adjacent participant gap from scratch based on
 * what's CURRENTLY needed (params on/off, current label content, current
 * participant widths) — grows OR SHRINKS, unlike a one-way "only widen if
 * broken" pass. This is what makes turning parameters back off (hotkey P)
 * followed by A actually shrink the schema back down again, not leave
 * whatever the widest state ever was (chat 2026-07-19).
 *
 * Only handles ADJACENT participants directly — a message spanning several
 * of them (skips over ones in between, common for a call reaching straight
 * into a resolved dependency, see CsSeqOrchestrator's third-level calls)
 * doesn't own any single gap to resize; those are handled separately by
 * widenNonAdjacentPairs below, grow-only, same as before this rewrite.
 * @param {object[][]} pairs
 * @param {{shape: object, unit: object}[]} participants
 * @returns {{shape: object, unit: object}[]} the same participants, re-sorted by (now current) x
 */
function normalizeGaps(pairs, participants) {
  const ordered = participants.slice().sort(function byX(a, b) { return a.shape.x - b.shape.x; });
  for (let i = 0; i < ordered.length - 1; i += 1) {
    const p1 = ordered[i];
    const p2 = ordered[i + 1];
    const requiredDistance = requiredDistanceBetween(p1, p2, pairs);
    const currentDistance = centerX(p2.shape) - centerX(p1.shape);
    const delta = requiredDistance - currentDistance;
    if (delta !== 0) {
      applyGapDelta(ordered, i, delta);
    }
  }
  return ordered;
}

/**
 * Grow-only fallback for pairs whose two participants AREN'T adjacent (a
 * multi-hop message, e.g. a third-level call reaching straight past one or
 * more participants) — normalizeGaps only resizes single adjacent gaps, so
 * a message like this doesn't own one to shrink/grow directly. Widens the
 * FIRST gap after its left-most endpoint if the message still doesn't fit
 * after normalizeGaps — any one of the gaps it spans growing wider fixes
 * the total span, picking the first keeps this simple (v1 approximation,
 * unchanged from before this rewrite; deliberately NOT bidirectional, since
 * shrinking a span covering several independent gaps isn't well-defined).
 * @param {object[][]} pairs
 * @param {object[]} activations
 * @param {{shape: object, unit: object}[]} ordered - participants sorted by CURRENT x
 */
function widenNonAdjacentPairs(pairs, activations, ordered) {
  pairs.forEach(function checkPair(pair) {
    const message = pair[0];
    const fromIndex = ordered.findIndex(function matchFrom(p) { return p.shape.id === message.fromParticipantId; });
    const toIndex = ordered.findIndex(function matchTo(p) { return p.shape.id === message.toParticipantId; });
    if (fromIndex === -1 || toIndex === -1 || Math.abs(fromIndex - toIndex) <= 1) {
      return; // adjacent (or unresolved) — already handled by normalizeGaps
    }
    const widths = pair.map(function measure(m) { return measureLabelWidth(fullDisplayText(m)); });
    const stillCollides = pair.some(function checkOne(m, i) { return labelCollidesWithActivations(m.x + START_MARGIN, widths[i], m, activations); });
    if (!stillCollides) return;
    const requiredWidth = START_MARGIN + Math.max.apply(null, widths) + END_MARGIN + WIDEN_BUFFER;
    const deficit = requiredWidth - message.w;
    if (deficit <= 0) return;
    applyGapDelta(ordered, Math.min(fromIndex, toIndex), deficit);
  });
}

/**
 * Adjusts the gap between ordered[index] and ordered[index+1] by `delta`
 * (positive grows, negative shrinks), cascading to everything positioned
 * further right, via the SAME engine calls a manual drag would use
 * (UmlCanvasEngine.updateShapePosition/resizeShape) — lifelines/activations/
 * messages/bottom-twins keep tracking automatically through the existing
 * CsSeqDiaRelationHandler cascade, nothing here mutates them directly.
 * @param {{shape: object, unit: object}[]} ordered - participants sorted by CURRENT x
 * @param {number} index
 * @param {number} delta
 */
function applyGapDelta(ordered, index, delta) {
  const p1 = ordered[index];
  const p2 = ordered[index + 1];
  if (!p2) return;

  if (p1.unit === p2.unit) {
    // Same package — resize its width FIRST (grow before moving anything
    // outward, so updateShapePosition's own containment re-check never sees
    // a participant as "escaped"; shrink is safe either order since nothing
    // moves further than the new, still-sufficient width), then shift p2 and
    // every LATER participant still inside that same package.
    umlCanvasEngine.resizeShape(p1.unit.id, p1.unit.w + delta, p1.unit.h);
    ordered.slice(index + 1).forEach(function shiftWithinPackage(p) {
      if (p.unit !== p1.unit) return;
      umlCanvasEngine.updateShapePosition(p.shape.id, p.shape.x + delta, p.shape.y);
    });
  }

  // Every OTHER unit (a different package, or a standalone actor) starting
  // at or after p2 shifts as a whole — moving a package cascades to every
  // one of its own children automatically, so this is one call per unit,
  // not per participant.
  const shiftedUnits = new Set();
  ordered.slice(index + 1).forEach(function shiftUnitOnce(p) {
    if (p.unit === p1.unit || shiftedUnits.has(p.unit.id)) return;
    shiftedUnits.add(p.unit.id);
    umlCanvasEngine.updateShapePosition(p.unit.id, p.unit.x + delta, p.unit.y);
  });
}

/**
 * STEP 3 — top-alignment. Every participant "belongs" at the same bottom
 * edge (shape.y + shape.h) — that's where CsSeqCanvasLayoutBuilder starts
 * every lifeline from, an actor's own taller box is already bottom-aligned
 * to it too (see layoutActor). Manually dragging a whole package (or a
 * standalone actor) up/down is the only way this drifts out of sync — moves
 * the OWNING unit (package or actor), never an individual participant
 * directly, so its lifeline/activations/messages cascade along for free via
 * the existing UmlCanvasEngine move machinery.
 * @param {{shape: object, unit: object}[]} participants
 */
function alignUnitsToTop(participants) {
  if (participants.length === 0) return;
  const bottoms = participants.map(function getBottom(p) { return p.shape.y + p.shape.h; });
  const target = Math.min.apply(null, bottoms); // align everyone to whichever already sits highest
  const adjustedUnits = new Set();
  participants.forEach(function alignOne(p, i) {
    const delta = target - bottoms[i];
    if (delta === 0 || adjustedUnits.has(p.unit.id)) return;
    adjustedUnits.add(p.unit.id);
    umlCanvasEngine.updateShapePosition(p.unit.id, p.unit.x, p.unit.y + delta);
  });
}

/**
 * Auto-layout for a sequence diagram already on canvas (post-Generate
 * cleanup step, hotkey A — see UmlCanvas.useConnectorKeyboardShortcuts.js).
 * Reads the CURRENT canvas geometry rather than assuming any prior state —
 * gaps are fully RECOMPUTED every run (see normalizeGaps), so this is safe
 * to run repeatedly as content changes (e.g. toggling parameters with P)
 * and will size things back down again, not just ever upward.
 *
 * Order: top-alignment first (consistent geometry for what follows), then
 * force every label to START_MARGIN, then normalize every adjacent gap
 * (grow or shrink) to fit that, then a grow-only fallback pass for any
 * multi-hop message that still doesn't fit.
 */
export function runAutoLayoutSeqDia() {
  const { participants, messages, activations } = collectSeqDiaData();
  if (participants.length === 0) return;

  alignUnitsToTop(participants);
  const pairs = buildPairs(messages);
  nudgeLabels(pairs);
  const ordered = normalizeGaps(pairs, participants);
  widenNonAdjacentPairs(pairs, activations, ordered);

  umlCanvasEngine.notify();
}
